<template>
  <b-container fluid class="p-0 h-100" style="overflow-y: auto;">
    <!-- Platforms list -->
    <div class="list-platforms">
      <h3 class="pt-3 pl-3 pb-2" v-if="!('embed' in this.$route.query)">
        Platforms <b-badge pill variant="primary">{{ platforms_count }}</b-badge>
        <span class="d-none d-sm-inline hint text-secondary">
          <font-awesome-icon :icon="['far', 'clock']" class="mr-1"></font-awesome-icon>
          All times in your local time
        </span>
        <b-button class="d-none d-lg-block float-right mr-3" variant="light" title="Minimize platforms list" @click="togglePlatformsBar"><font-awesome-icon :icon="['fas', 'angle-double-left']" /></b-button>
      </h3>
      <h4 class="pt-3 pl-2 pb-1" v-else>
        <span >
          <img src="/beluga-web/static/geomar_logo.png" alt="Logo" style="height: 35px" class="mr-1 align-baseline">
          <span v-if="this.campaignSelected" class="d-inline-block">
            <div>
              {{ this.campaignSelected.short_name }}
              <a class="text-muted" :href="'https://beluga.geomar.de/' + this.campaignSelected.url_id" target="_blank" title="Open campaign in new window">
                <font-awesome-icon style="vertical-align: baseline; font-size: 15px; color: #6c757d;" :icon="['fas', 'external-link-alt']"></font-awesome-icon>
              </a>
            </div>
            <div class="ml-3" style="font-size: 11px; color: #6c757d;">
              Visualized by <a href="https://www.geomar.de/st/auv/beluga" target="_blank"><strong>BELUGA</strong></a>
            </div>
          </span>
          <span v-else>No campaign selected</span>
        </span>
        <!--<span ref="current_time" class="d-none d-sm-inline clock">00:00:00</span>-->
        <b-button class="d-none d-lg-block float-right mr-3" variant="light" title="Minimize platforms list" @click="togglePlatformsBar"><font-awesome-icon :icon="['fas', 'angle-double-left']" /></b-button>
      </h4>
      <b-alert v-if="platforms_count === 0" show variant="warning" class="mt-2 ml-3 mr-3">
        <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="mr-1" /> No platforms associated with selected campaign.
      </b-alert>
      <div role="tablist" class="accordion">
        <b-card no-body v-for="platform in platforms" :key="platform.id" class="rounded-0 border-left-0 border-right-0">
          <b-card-header header-tag="header" class="collapse d-flex rounded-0 p-0 shadow-sm" role="tab">
            <platforms-list-header-widget :platform-id="platform.id" :full="true"></platforms-list-header-widget>
          </b-card-header>
          <b-collapse :id="'dashboard-' + platform.id" :visible="platform_visible(platform.id)" accordion="accordion-platforms" role="tabpanel">
            <b-card-body class="p-0">
              <div class="d-md-flex d-none ml-3 mr-3 mt-1 border-bottom justify-content-between">
                <span class="text-muted">
                  <small>ID: <strong>{{ platform.id }}</strong></small>
                </span>
                <span class="text-muted">
                  <small>Length: <strong>{{ showDigits(platform.length, 1) }} m</strong></small>
                </span>
                <span class="text-muted">
                  <small>Width: <strong>{{ showDigits(platform.width, 1) }} m</strong></small>
                </span>
                <span class="text-muted">
                  <small>Height: <strong>{{ showDigits(platform.height, 1) }} m</strong></small>
                </span>
                <span class="text-muted">
                  <small>Draught: <strong>{{ showDigits(platform.draught, 1) }} m</strong></small>
                </span>
              </div>
              <b-card-text>
                <div v-if="typeof platform.last_change === 'undefined'">
                  <b-alert variant="info" show class="pl-3 pt-2 pb-2 m-3">
                    <font-awesome-icon :icon="['fas', 'spinner']" class="mr-1 fa-spin" size="sm" />
                    Waiting for data..
                  </b-alert>
                </div>
                <div v-else>
                  <b-tabs pills small nav-wrapper-class="pl-3 pt-2 pb-2 border-bottom platform-pages" :id="'platform-pages-' + platform.id">
                    <!--<b-tab :active="platform.type === 'ship-dship' && Number(platform.id) === 211206980" v-if="platform.type === 'ship-dship' && Number(platform.id) === 211206980">
                      <template v-slot:title>
                        ROV Camera
                        <sup><b-badge variant="danger">Live</b-badge></sup>
                      </template>
                      <ship-d-ship-livestream-widget :platform-id="platform.id"></ship-d-ship-livestream-widget>
                    </b-tab>-->
                    <b-tab active title="Dashboard" class="p-0">
                      <!-- TODO: Dynamic? -->
                      <auv-dashboard-widget v-if="platform.type === 'auv-g500'" :platform-id="platform.id"></auv-dashboard-widget>
                      <transponder-dashboard-widget v-else-if="platform.type === 'transponder'" :platform-id="platform.id"></transponder-dashboard-widget>
                      <ship-dashboard-widget v-else-if="platform.type === 'ship'" :platform-id="platform.id"></ship-dashboard-widget>
                      <ship-marine-traffic-dashboard-widget v-else-if="platform.type === 'ship-mt'" :platform-id="platform.id"></ship-marine-traffic-dashboard-widget>
                      <ship-d-ship-dashboard-widget v-else-if="platform.type === 'ship-dship'" :platform-id="platform.id"></ship-d-ship-dashboard-widget>
                      <ship-dagmar-aaen-dashboard-widget v-else-if="platform.type === 'ship-dagmaraaen'" :platform-id="platform.id"></ship-dagmar-aaen-dashboard-widget>
                      <ship-citizen-science-dashboard-widget v-else-if="platform.type.startsWith('ship-citizen-science')" :platform-id="platform.id"></ship-citizen-science-dashboard-widget>
                      <ship-ocean-race-dashboard-widget v-else-if="platform.type === 'ship-tor'" :platform-id="platform.id"></ship-ocean-race-dashboard-widget>
                      <saildrone-dashboard-widget v-else-if="platform.type === 'saildrone'" :platform-id="platform.id"></saildrone-dashboard-widget>
                      <waveglider-dashboard-widget v-else-if="platform.type === 'waveglider'" :platform-id="platform.id"></waveglider-dashboard-widget>
                      <ship-eugen-seibold-dashboard-widget v-else-if="platform.type === 'ship-eugenseibold'" :platform-id="platform.id"></ship-eugen-seibold-dashboard-widget>
                      <ship-iow-dashboard-widget v-else-if="platform.type === 'ship-iow'" :platform-id="platform.id"></ship-iow-dashboard-widget>
                      <logger-hyfive-dashboard-widget v-else-if="platform.type ==='logger-hyfive'" :platform-id="platform.id"></logger-hyfive-dashboard-widget>
                      <buoy-c-v-o-o1-dashboard-widget v-else-if="platform.type ==='buoy-cvoo1'" :platform-id="platform.id"></buoy-c-v-o-o1-dashboard-widget>
                      <generic-dashboard-widget v-else :platform-id="platform.id"></generic-dashboard-widget>
                    </b-tab>
                    <b-tab title="Data" class="p-0">
                      <generic-entities-widget :platform-id="platform.id"></generic-entities-widget>
                    </b-tab>
                    <b-tab v-if="platform.type === 'ship-dagmaraaen'">
                      <template v-slot:title>
                        Camera
                        <sup><b-badge variant="danger">Live</b-badge></sup>
                      </template>
                      <ship-dagmar-aaen-livestream-widget :platform-id="platform.id"></ship-dagmar-aaen-livestream-widget>
                    </b-tab>
                    <b-tab v-if="platform.type === 'auv-g500'" title="Missions">
                      <auv-missions-widget :platform-id="platform.id"></auv-missions-widget>
                    </b-tab>
                    <b-tab v-if="platform.type === 'auv-g500'" title="Tracker" disabled>
                      <!-- Tracker widget -->
                    </b-tab>
                    <b-tab v-if="platform.type === 'transponder'" title="Calibration" disabled>
                      <!-- Tracker widget -->
                    </b-tab>
                    <!--<b-tab title="Log" disabled></b-tab>-->
                  </b-tabs>
                </div>
              </b-card-text>
            </b-card-body>
          </b-collapse>
        </b-card>
      </div>
      <div class="mt-2 text-secondary" v-if="platforms_count > 0 && this.selectedPlatform === 0" style="margin-left: 40px;">
        <font-awesome-icon :icon="['fas', 'angle-double-up']" class="mr-1" size="sm" />
        Select platform to open its dashboard
      </div>
      <div v-else style="height: 89px;"></div>
    </div>
    <div class="footer pt-2 pb-2 pr-3 pl-3 border-top d-inline-flex justify-content-between" style="background-color: #f8f9fa;">
      <span class="text-muted" style="width: 440px">&copy; 2025 <strong>GEOMAR</strong> <span class="d-none d-sm-inline-block">
        Helmholtz Centre for Ocean Research</span> Kiel <br />
        <!--Die Verwendung der Marke BELUGA erfolgt mit freundlicher Genehmigung der Martin Schönbeck Beratungen GmbH, Spenge.-->
        The use of the trademark BELUGA is with the kind permission of Martin Schönbeck Beratungen GmbH, Spenge, Germany.
      </span>
      <a href="https://www.geomar.de/en/impressum" target="_blank" style="width: 75px">
        <strong>Imprint</strong> <font-awesome-icon :icon="['fas', 'external-link-alt']" size="xs" class="align-baseline ml-1" />
      </a>
    </div>
    <div v-if="!('commands' in this.$route.query)" class="footer" style="margin-bottom: 89px;">
      <b-alert show variant="warning" dismissible class="m-0">These raw data have not been subjected to our quality control or quality assurance procedures. They are released for limited public use as preliminary data to be used only with appropriate caution.</b-alert>
    </div>
    <!-- Targets list -->
<!--    <div v-if="targets_count > 0 && targets_visible === true" class="list-targets">-->
<!--      <h3 class="pt-3 pl-3 pb-2">-->
<!--        AIS Targets <b-badge pill variant="primary">{{ targets_count }}</b-badge>-->
<!--      </h3>-->
<!--      <div role="tablist" class="accordion" style="overflow-y: auto; max-height: 222px;" >-->
<!--        <b-card no-body v-for="target in targets" :key="target.id" class="rounded-0 border-left-0 border-right-0">-->
<!--          <b-card-header style="display: table;" header-tag="header" class="collapse d-flex rounded-0 p-0 shadow-sm" role="tab">-->
<!--            <platforms-list-header-widget :platform-id="target.id"></platforms-list-header-widget>-->
<!--          </b-card-header>-->
<!--          <b-collapse :id="'dashboard-' + target.id" :visible="platform_visible(target.id)" accordion="accordion-targets" role="tabpanel">-->
<!--            <b-card-body class="p-0">-->
<!--              <b-card-title>-->
<!--                <generic-dashboard-widget :platform-id="target.id"></generic-dashboard-widget>-->
<!--              </b-card-title>-->
<!--              <b-card-text>-->
<!--                <entities-widget :platform-id="target.id"></entities-widget>-->
<!--              </b-card-text>-->
<!--            </b-card-body>-->
<!--          </b-collapse>-->
<!--        </b-card>-->
<!--      </div>-->
<!--    </div>-->
  </b-container>
</template>

<script>
import moment from 'moment'
import { mapGetters, mapMutations, mapState } from 'vuex'
import Helpers from '@/mixins/helpers'
import PlatformsListHeaderWidget from '@/components/widgets/PlatformsListHeaderWidget'
import GenericDashboardWidget from '@/components/widgets/generic/GenericDashboardWidget'
import GenericEntitiesWidget from '@/components/widgets/generic/GenericEntitiesWidget'
import AuvMissionsWidget from '@/components/widgets/auv/AuvMissionsWidget.vue'
import AuvDashboardWidget from '@/components/widgets/auv/AuvDashboardWidget'
import TransponderDashboardWidget from '@/components/widgets/transponder/TransponderDashboardWidget'
import ShipDashboardWidget from '@/components/widgets/ship/ShipDashboardWidget'
import ShipDShipDashboardWidget from '@/components/widgets/ship/ShipDShipDashboardWidget.vue'
// import ShipDShipLivestreamWidget from '@/components/widgets/ship/ShipDShipLivestreamWidget.vue'
import ShipDagmarAaenDashboardWidget from '@/components/widgets/ship/ShipDagmarAaenDashboardWidget.vue'
import ShipCitizenScienceDashboardWidget from '@/components/widgets/ship/ShipCitizenScienceDashboardWidget.vue'
import ShipOceanRaceDashboardWidget from '@/components/widgets/ship/ShipOceanRaceDashboardWidget.vue'
import ShipMarineTrafficDashboardWidget from '@/components/widgets/ship/ShipMarineTrafficDashboardWidget.vue'
import SaildroneDashboardWidget from '@/components/widgets/saildrone/SaildroneDashboardWidget'
import WavegliderDashboardWidget from '@/components/widgets/waveglider/WavegliderDashboardWidget'
import ShipDagmarAaenLivestreamWidget from '@/components/widgets/ship/ShipDagmarAaenLivestreamWidget.vue'
import ShipEugenSeiboldDashboardWidget from '@/components/widgets/ship/ShipEugenSeiboldDashboardWidget.vue'
import LoggerHyfiveDashboardWidget from '@/components/widgets/loggers/LoggerHyfiveDashboardWidget.vue'
import ShipIowDashboardWidget from '@/components/widgets/ship/ShipIowDashboardWidget.vue'
import BuoyCVOO1DashboardWidget from '@/components/widgets/buoy/BuoyCVOO1DashboardWidget.vue'

// const UPDATE_INTERVAL_MS = 1000

export default {
  name: 'PlatformsOpenWidget',
  mixins: [
    Helpers
  ],
  components: {
    BuoyCVOO1DashboardWidget,
    ShipIowDashboardWidget,
    LoggerHyfiveDashboardWidget,
    ShipDagmarAaenLivestreamWidget,
    GenericDashboardWidget,
    GenericEntitiesWidget,
    PlatformsListHeaderWidget,
    ShipDashboardWidget,
    ShipDagmarAaenDashboardWidget,
    ShipCitizenScienceDashboardWidget,
    ShipDShipDashboardWidget,
    // ShipDShipLivestreamWidget,
    ShipOceanRaceDashboardWidget,
    ShipMarineTrafficDashboardWidget,
    ShipEugenSeiboldDashboardWidget,
    TransponderDashboardWidget,
    AuvDashboardWidget,
    AuvMissionsWidget,
    SaildroneDashboardWidget,
    WavegliderDashboardWidget
  },
  data: function () {
    return {}
  },
  mounted: function () {
    // Initialize last change timestamp
    // this.updateClock()
    // Start last change timestamp updater
    // this.updateTask = setInterval(this.updateClock, UPDATE_INTERVAL_MS)
  },
  beforeDestroy: function () {
    // Remove background job again
    clearInterval(this.updateTask)
  },
  methods: {
    ...mapMutations(['toggle_collapse']),
    togglePlatformsBar: function () {
      this.toggle_collapse('sidebar-platforms')
    },
    // comparePlatformTypes: function (platform1, platform2) {
    //   if (platform1.type.startsWith('ship') && !platform2.type.startsWith('ship')) {
    //     return true
    //   }
    //   return false
    // },
    updateClock: function () {
      this.$refs.current_time.innerHTML = moment().utc().format('HH:mm:ss') + ' <small>UTC</small>'
    }
  },
  computed: {
    ...mapState(['selectedCampaign', 'selectedPlatform']),
    ...mapGetters(['campaignSelected', 'campaignPlatforms', 'countPlatforms', 'targets', 'countTargets']),
    platforms: function () {
      return this.campaignPlatforms(this.selectedCampaign)
      // return platforms.sort(this.comparePlatformTypes)
    },
    platforms_count: function () {
      return this.campaignPlatforms(this.selectedCampaign).length
    },
    targets_count: function () {
      return this.countTargets
    },
    // targets_visible: function () {
    //  return (this.campaignSelected.option_ais === '1')
    // },
    platform_visible: function () {
      return (platformId) => {
        return Number(platformId) === Number(this.selectedPlatform)
      }
    },
    // watercolumn_widget_width: function () {
    //  return this.platforms_count_watercolumn === 0 ? '70px' : 'calc(60px * ' + this.platforms_count_watercolumn + ')'
    // },
    platforms_count_watercolumn: function () {
      // Only count platforms that can dive
      // TODO: Optimize
      let i = 0
      for (const platform in this.platforms) {
        if (Object.prototype.hasOwnProperty.call(this.platforms, platform)) {
          if (this.platforms[platform].type === 'auv-g500' ||
            this.platforms[platform].type === 'transponder') {
            i = i + 1
          }
        }
      }
      return i
    }
  }
}
</script>

<style>

.hint {
  font-size: 16px;
  margin-left: 70px;
  font-weight: normal;
}

.footer {
  position: absolute;
  bottom: 0;
  width: 100%;
}

.nav-pills .nav-link.active, .nav-pills .show>.nav-link {
  background-color: transparent !important;
  border: 1px solid #007bff;
  color: #007bff !important;
}

</style>
